import { Button, Card, Input, Space, Typography, Divider, Select, Form, Popconfirm, Alert, Spin, message } from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import DashboardLayout from "layout/DashboardLayout";
import { useState, useEffect, useRef } from "react";
import { TbArrowsLeftRight, TbPlus, TbTrash, TbRefresh } from "react-icons/tb";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { queryClient } from "utils/queryClient";

interface MappingEntry {
  id: string;
  key: string;
  value: string;
  type: "value" | "image";
  isNew?: boolean;
}

function KeyValueMapper() {
  const [form] = Form.useForm();
  const [templateId, setTemplateId] = useState("");
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>();
  const [signatureFields, setSignatureFields] = useState<any[]>([]);

  const getSavedMappings = ApiService.pandadoc.useGetOrgMappings(searchParams.get("org_code"));
  const templateKeysQuery = ApiService.pandadoc.useGetPandaDocTemplateKeys(
    useDebounce(templateId, 600)[0],
    searchParams.get("org_code"),
  );
  const fnsResult = ApiService.pandadoc.useGetPandaDocFunctions();
  const createMappingMutation = ApiService.pandadoc.useCreateMappings();

  const [mappings, setMappings] = useState<MappingEntry[]>([]);

  useEffect(() => {
    const org_code = searchParams.get("org_code");
    if (org_code) form.setFieldsValue({ orgCode: org_code });
  }, [searchParams]);

  useEffect(() => {
    const result = templateKeysQuery.data?.result;
    if (!result) return;

    const { tokens, existing_map, signature_fields } = result;
    const hasExisting = existing_map && Object.keys(existing_map).length > 0;

    if (hasExisting) {
      const entries = Object.entries(existing_map).map(([key, val]: any) => ({
        id: crypto.randomUUID(),
        key,
        value: val.fn,
        type: val.type,
      }));
      setMappings(entries);
    } else {
      const entries = tokens.map((key: string) => ({
        id: crypto.randomUUID(),
        key,
        value: "",
        type: "value" as const,
      }));
      setMappings(entries);
    }

    if (Array.isArray(signature_fields) && signature_fields.length) {
      setSignatureFields((prev) => (prev.length ? prev : signature_fields));
    }
  }, [templateKeysQuery.data]);

  useEffect(() => {
    if (templateKeysQuery.isError) {
      message.error(templateKeysQuery.error.response?.data.message);
    }
  }, [templateKeysQuery.isError]);

  const functionOptions =
    fnsResult.data?.result?.map((fn: any) => ({
      label: fn?.name ?? fn,
      value: fn?.name ?? fn,
    })) ?? [];

  const typeOptions = [
    { label: "Value", value: "value" },
    { label: "Image URL", value: "image" },
  ];

  const handleSync = async () => {
    const result = templateKeysQuery.data?.result;
    if (!result) return;

    const { tokens } = result;

    const existingKeys = new Set(mappings.map((m) => m.key));

    const newEntries = tokens
      .filter((k: string) => !existingKeys.has(k))
      .map((key: string) => ({
        id: crypto.randomUUID(),
        key,
        value: "",
        type: "value" as const,
        isNew: true,
      }));

    if (!newEntries.length) {
      message.info("No new keys found");
      return;
    }

    setMappings((prev) => [...prev, ...newEntries]);

    message.success(`${newEntries.length} new keys added from PandaDoc`);

    setTimeout(() => {
      const el = document.getElementById(newEntries[0].id);
      if (el && containerRef.current) {
        containerRef.current.scrollTo({
          top: el.offsetTop - 100,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleGenerate = async () => {
    try {
      const values = await form.validateFields();

      const map: Record<string, { fn: string; type: "value" | "image" }> = {};

      mappings.forEach((m) => {
        if (m.key && m.value) {
          map[m.key] = {
            fn: m.value,
            type: m.type,
          };
        }
      });

      const hasInvalidSignature = signatureFields.some(
        (f) =>
          !f.field_id ||
          !f.layout?.page ||
          !f.layout?.position?.anchor_point ||
          f.layout?.position?.offset_x === undefined ||
          f.layout?.position?.offset_y === undefined ||
          f.layout?.style?.width === undefined ||
          f.layout?.style?.height === undefined,
      );

      if (hasInvalidSignature) {
        message.error("Fill all signature fields properly.");
        return;
      }

      await createMappingMutation.mutateAsync({
        templateId: values.templateId,
        org_code: values.orgCode,
        map,
        title: values.title,
        signature_fields: signatureFields,
      });

      queryClient.invalidateQueries({
        queryKey: ["/pandadoc/saved-mappings", values.orgCode],
      });

      setTemplateId(values.templateId);
      form.setFieldsValue({ templateId: values.templateId });

      setMappings((prev) =>
        prev.map((m) => ({
          ...m,
          isNew: false,
        })),
      );

      message.success("Mappings generated successfully.");
    } catch {
      message.error("Unable to generate mappings.");
    }
  };

  const updateMapping = (id: string, field: keyof MappingEntry, value: any) => {
    setMappings((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  };

  const addMapping = () =>
    setMappings((prev) => [...prev, { id: crypto.randomUUID(), key: "", value: "", type: "value" }]);

  const removeMapping = (id: string) => mappings.length > 1 && setMappings((prev) => prev.filter((m) => m.id !== id));

  const clearAllMappings = () => setMappings([]);

  const canSync = templateKeysQuery.data?.result?.tokens?.some((k: string) => !mappings.some((m) => m.key === k));

  const isInvalid = mappings.some((m) => !m.key || !m.value);

  const addSignatureField = () => {
    setSignatureFields((prev) => [
      ...prev,
      {
        type: "signature",
        field_id: "",
        layout: {
          position: {
            anchor_point: "",
            offset_x: undefined,
            offset_y: undefined,
          },
          page: undefined,
          style: {
            width: undefined,
            height: undefined,
          },
        },
      },
    ]);
  };

  return (
    <DashboardLayout>
      <PageTitle
        title="PandaDoc Mapper"
        pageTitle={[{ title: "Organisations", link: "/dashboard/organisations" }, { title: "Template Mapper" }]}
      />

      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <Typography.Title level={2}>PandaDoc Template Mapper</Typography.Title>
          <Typography.Text type="secondary">Map PandaDoc template variables to application functions</Typography.Text>

          <Card className="mt-4">
            <Form layout="vertical" form={form}>
              <div className="grid grid-cols-4 gap-4 mb-2">
                <Form.Item label="Saved Templates">
                  <Select
                    placeholder="Select existing template"
                    value={selectedTemplate}
                    onChange={(val) => {
                      const selected = getSavedMappings.data?.result?.find((m: any) => m.templateId === val);

                      setSelectedTemplate(val || undefined);
                      setTemplateId(val || "");

                      form.setFieldsValue({
                        templateId: val,
                        title: selected?.title || "",
                      });
                    }}
                    allowClear
                    options={
                      getSavedMappings.data?.result?.map((m: any) => ({
                        label: m.title || m.templateId,
                        value: m.templateId,
                      })) ?? []
                    }
                    // onChange={(val) => {
                    //   setTemplateId(val || "");
                    //   form.setFieldsValue({ templateId: val });
                    // }}
                  />
                </Form.Item>

                <Form.Item label="Template Name" name="title" rules={[{ required: true }]}>
                  <Input placeholder="Title for this template" />
                </Form.Item>

                <Form.Item label="Template ID" name="templateId" rules={[{ required: true }]}>
                  <Input
                    value={templateId}
                    onChange={(e) => {
                      setTemplateId(e.target.value);
                      setSelectedTemplate(undefined);
                      form.setFieldsValue({
                        title: "",
                      });
                    }}
                    suffix={templateKeysQuery.isFetching ? <Spin size="small" /> : null}
                    placeholder="Enter PandaDoc template ID to autofill keys"
                  />
                </Form.Item>

                <Form.Item label="Organisation Code" name="orgCode" rules={[{ required: true }]}>
                  <Input disabled />
                </Form.Item>
              </div>

              <div className="flex justify-end mb-4">
                <Button
                  icon={<TbRefresh />}
                  loading={templateKeysQuery.isFetching}
                  onClick={handleSync}
                  disabled={!templateId || !canSync}
                >
                  Fetch template keys from PandaDoc
                </Button>
              </div>

              <Divider />

              <div className="flex justify-between mb-3">
                <Typography.Text strong>Key Mappings</Typography.Text>

                <div className="flex gap-2">
                  <Popconfirm title="Clear mappings?" onConfirm={clearAllMappings}>
                    <Button danger size="small">
                      Clear All
                    </Button>
                  </Popconfirm>
                  <Button icon={<TbPlus />} size="small" onClick={addMapping}>
                    Add
                  </Button>
                </div>
              </div>

              <div ref={containerRef} className="max-h-[420px] overflow-y-auto pr-2">
                {mappings.length === 0 ? (
                  <Typography.Text type="secondary">
                    Enter a Template ID to load keys or select an existing template to edit mappings
                  </Typography.Text>
                ) : (
                  <Space direction="vertical" className="w-full">
                    {mappings.map((m) => (
                      <div
                        key={m.id}
                        id={m.id}
                        className={`grid grid-cols-[1fr_40px_1fr_120px_auto] gap-2 items-center ${
                          m.isNew ? "bg-yellow-50 border border-yellow-200 rounded px-2 py-1" : ""
                        }`}
                      >
                        <Input value={m.key} onChange={(e) => updateMapping(m.id, "key", e.target.value)} />

                        <div className="flex items-center justify-center">
                          <TbArrowsLeftRight size={18} className="text-gray-400" />
                        </div>

                        <Select
                          value={m.value}
                          options={functionOptions}
                          loading={fnsResult.isLoading}
                          onChange={(v) => updateMapping(m.id, "value", v)}
                          showSearch
                        />

                        <Select value={m.type} options={typeOptions} onChange={(v) => updateMapping(m.id, "type", v)} />

                        <Button danger icon={<TbTrash />} onClick={() => removeMapping(m.id)} />
                      </div>
                    ))}
                  </Space>
                )}
              </div>

              <Divider />

              <Typography.Text strong>Signature Fields</Typography.Text>

              <Space direction="vertical" className="w-full mt-2">
                {signatureFields.map((field, idx) => (
                  <div key={idx} className="grid grid-cols-8 gap-2">
                    <Input
                      placeholder="Field ID"
                      value={field.field_id}
                      onChange={(e) => {
                        const u = [...signatureFields];
                        u[idx].field_id = e.target.value;
                        setSignatureFields(u);
                      }}
                    />

                    <Select
                      placeholder="Anchor"
                      value={field.layout?.position?.anchor_point}
                      onChange={(val) => {
                        const u = [...signatureFields];
                        u[idx].layout.position.anchor_point = val;
                        setSignatureFields(u);
                      }}
                      options={[
                        { label: "Top Left", value: "topleft" },
                        { label: "Top Right", value: "topright" },
                        { label: "Bottom Left", value: "bottomleft" },
                        { label: "Bottom Right", value: "bottomright" },
                      ]}
                    />

                    <Input
                      placeholder="Page"
                      value={field.layout?.page}
                      type="number"
                      onChange={(e) => {
                        const u = [...signatureFields];
                        u[idx].layout.page = Number(e.target.value);
                        setSignatureFields(u);
                      }}
                    />

                    <Input
                      placeholder="Offset X"
                      value={field.layout?.position?.offset_x}
                      type="number"
                      onChange={(e) => {
                        const u = [...signatureFields];
                        u[idx].layout.position.offset_x = Number(e.target.value);
                        setSignatureFields(u);
                      }}
                    />

                    <Input
                      placeholder="Offset Y"
                      value={field.layout?.position?.offset_y}
                      type="number"
                      onChange={(e) => {
                        const u = [...signatureFields];
                        u[idx].layout.position.offset_y = Number(e.target.value);
                        setSignatureFields(u);
                      }}
                    />

                    <Input
                      placeholder="Width"
                      type="number"
                      value={field.layout?.style?.width}
                      onChange={(e) => {
                        const u = [...signatureFields];
                        u[idx].layout.style.width = Number(e.target.value);
                        setSignatureFields(u);
                      }}
                    />

                    <Input
                      placeholder="Height"
                      type="number"
                      value={field.layout?.style?.height}
                      onChange={(e) => {
                        const u = [...signatureFields];
                        u[idx].layout.style.height = Number(e.target.value);
                        setSignatureFields(u);
                      }}
                    />

                    <Button
                      danger
                      onClick={() => {
                        setSignatureFields((prev) => prev.filter((_, i) => i !== idx));
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </Space>

              <Button className="ml-2 mt-4" onClick={addSignatureField}>
                Add Signature Field
              </Button>

              <Divider />

              <Button
                type="primary"
                block
                loading={createMappingMutation.isPending || templateKeysQuery.isFetching}
                onClick={handleGenerate}
                disabled={!templateId || isInvalid || templateKeysQuery.isFetching || mappings.length === 0}
              >
                Save PandaDoc Tokens
              </Button>
            </Form>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default KeyValueMapper;
